<template>
  <section
    class="role-dashboard"
    :style="dashboardStyle"
  >
    <b-row class="match-height">
      <b-col
        cols="12"
        xl="8"
      >
        <b-card class="role-dashboard__hero mb-2 border-0">
          <div class="d-flex flex-column flex-lg-row justify-content-between">
            <div class="pr-lg-2">
              <div class="role-dashboard__eyebrow mb-1">
                {{ config.eyebrow }}
              </div>
              <h2 class="text-white mb-1">
                {{ config.heroTitle }}
              </h2>
              <p class="role-dashboard__hero-copy mb-2">
                {{ config.heroSummary }}
              </p>

              <div class="d-flex flex-wrap">
                <div
                  v-for="highlight in config.highlights"
                  :key="`${dashboardKey}-${highlight.label}`"
                  class="role-dashboard__highlight"
                >
                  <div class="role-dashboard__highlight-value">
                    {{ highlight.value }}
                  </div>
                  <div class="role-dashboard__highlight-label">
                    {{ highlight.label }}
                  </div>
                </div>
              </div>
            </div>

            <div class="role-dashboard__hero-aside mt-2 mt-lg-0">
              <div class="role-dashboard__hero-aside-label mb-50">
                Signed in as
              </div>
              <div class="role-dashboard__hero-aside-name">
                {{ userName }}
              </div>
              <div class="role-dashboard__hero-aside-role mb-1">
                {{ currentRole }}
              </div>
              <div class="role-dashboard__hero-aside-context">
                {{ workContext }}
              </div>
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
        cols="12"
        xl="4"
      >
        <b-card class="role-dashboard__context-card mb-2 h-100 border-0">
          <div class="role-dashboard__context-pill mb-1">
            Role dashboard
          </div>
          <h4 class="mb-1">
            {{ config.calloutTitle }}
          </h4>
          <p class="mb-2 text-muted">
            {{ config.calloutText }}
          </p>

          <div class="role-dashboard__context-box">
            <div class="small text-muted mb-50">
              Current context
            </div>
            <div class="font-weight-bolder">
              {{ workContext }}
            </div>
            <div class="small text-muted mt-50">
              Personalized from the logged-in user profile and role assignment.
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-row class="match-height">
      <b-col
        v-for="stat in config.statCards"
        :key="`${dashboardKey}-${stat.label}`"
        cols="12"
        md="6"
        xl="3"
      >
        <b-card class="role-dashboard__stat-card mb-2 h-100">
          <div class="d-flex align-items-start justify-content-between">
            <div>
              <div class="role-dashboard__stat-value">
                {{ stat.value }}
              </div>
              <div class="font-weight-bolder mb-25">
                {{ stat.label }}
              </div>
              <div class="text-muted small">
                {{ stat.note }}
              </div>
            </div>

            <div class="role-dashboard__stat-icon">
              <feather-icon :icon="stat.icon" />
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-row class="match-height">
      <b-col
        v-for="(section, index) in config.sections"
        :key="`${dashboardKey}-${section.type}-${index}`"
        cols="12"
        :class="[section.colClass || 'lg-6']"
      >
        <b-card class="role-panel mb-2 h-100">
          <div class="d-flex align-items-start justify-content-between mb-50">
            <div>
              <b-card-title class="mb-0">
                {{ section.title }}
              </b-card-title>
              <b-card-text
                v-if="section.subtitle"
                class="small text-muted mb-0 mt-50"
              >
                {{ section.subtitle }}
              </b-card-text>
            </div>

            <b-badge
              v-if="section.badge"
              pill
              :variant="section.badgeVariant || 'primary'"
            >
              {{ section.badge }}
            </b-badge>
          </div>

          <div v-if="section.type === 'focus-list'">
            <div
              v-for="item in section.items"
              :key="`${section.title}-${item.title}`"
              class="role-panel__row"
            >
              <div class="d-flex align-items-start justify-content-between">
                <div class="pr-1">
                  <div class="font-weight-bolder">
                    {{ item.title }}
                  </div>
                  <div class="text-muted small mt-25">
                    {{ item.meta }}
                  </div>
                </div>

                <b-badge
                  pill
                  :variant="item.variant || 'primary'"
                >
                  {{ item.status }}
                </b-badge>
              </div>
            </div>
          </div>

          <div v-else-if="section.type === 'progress-list'">
            <div
              v-for="item in section.items"
              :key="`${section.title}-${item.label}`"
              class="role-panel__row"
            >
              <div class="d-flex align-items-center justify-content-between mb-50">
                <div class="font-weight-bolder">
                  {{ item.label }}
                </div>
                <div class="small font-weight-bolder">
                  {{ item.value }}%
                </div>
              </div>

              <b-progress
                height="8px"
                class="mb-50"
              >
                <b-progress-bar
                  :value="item.value"
                  :variant="item.variant || 'primary'"
                />
              </b-progress>

              <div class="text-muted small">
                {{ item.note }}
              </div>
            </div>
          </div>

          <div v-else-if="section.type === 'timeline'">
            <div
              v-for="item in section.items"
              :key="`${section.title}-${item.time}-${item.title}`"
              class="role-timeline__item"
            >
              <div class="d-flex align-items-center justify-content-between">
                <div class="font-weight-bolder">
                  {{ item.title }}
                </div>
                <b-badge
                  pill
                  :variant="item.variant || 'primary'"
                >
                  {{ item.time }}
                </b-badge>
              </div>
              <div class="text-muted small mt-25">
                {{ item.detail }}
              </div>
            </div>
          </div>

          <div v-else-if="section.type === 'action-grid'">
            <b-row>
              <b-col
                v-for="item in section.items"
                :key="`${section.title}-${item.title}`"
                cols="12"
                md="6"
                class="mb-1 mb-md-2"
              >
                <div class="role-action">
                  <div class="role-action__icon mb-1">
                    <feather-icon :icon="item.icon" />
                  </div>
                  <div class="font-weight-bolder mb-50">
                    {{ item.title }}
                  </div>
                  <div class="text-muted small mb-0">
                    {{ item.detail }}
                  </div>
                </div>
              </b-col>
            </b-row>
          </div>

          <div v-else-if="section.type === 'status-grid'">
            <b-row>
              <b-col
                v-for="item in section.items"
                :key="`${section.title}-${item.label}`"
                cols="12"
                md="6"
                class="mb-1 mb-md-2"
              >
                <div class="role-status">
                  <div class="d-flex align-items-center justify-content-between mb-1">
                    <div class="role-action__icon">
                      <feather-icon :icon="item.icon" />
                    </div>
                    <div class="role-status__value">
                      {{ item.value }}
                    </div>
                  </div>
                  <div class="font-weight-bolder mb-25">
                    {{ item.label }}
                  </div>
                  <div class="text-muted small">
                    {{ item.detail }}
                  </div>
                </div>
              </b-col>
            </b-row>
          </div>

          <div
            v-else-if="section.type === 'table'"
            class="table-responsive"
          >
            <table class="table table-borderless role-dashboard__table mb-0">
              <thead>
                <tr>
                  <th
                    v-for="column in section.columns"
                    :key="`${section.title}-${column}`"
                  >
                    {{ column }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, rowIndex) in section.rows"
                  :key="`${section.title}-${rowIndex}`"
                >
                  <td
                    v-for="(cell, cellIndex) in row"
                    :key="`${section.title}-${rowIndex}-${cellIndex}`"
                  >
                    {{ cell }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import {
    BBadge,
    BCard,
    BCardText,
    BCardTitle,
    BCol,
    BProgress,
    BProgressBar,
    BRow,
} from 'bootstrap-vue'
import { getPrimaryUserRole, getUserData } from '@/auth/utils'
import { getRoleDashboardConfig } from './roleDashboardConfigs'

export default {
    components: {
        BBadge,
        BCard,
        BCardText,
        BCardTitle,
        BCol,
        BProgress,
        BProgressBar,
        BRow,
    },
    props: {
        dashboardKey: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            userData: getUserData() || {},
        }
    },
    computed: {
        config() {
            return getRoleDashboardConfig(this.dashboardKey)
        },
        currentRole() {
            return getPrimaryUserRole(this.userData) || this.config.roleLabel
        },
        userName() {
            return this.userData.fullName || this.userData.firstName || this.userData.email || 'System User'
        },
        workContext() {
            if (this.userData.station && this.userData.station.station) {
                return this.userData.station.station.name
            }

            if (this.userData.location && this.userData.location.name) {
                return this.userData.location.name
            }

            if (this.userData.client && this.userData.client.name) {
                return this.userData.client.name
            }

            return 'System-wide operations'
        },
        dashboardStyle() {
            return {
                '--role-accent': this.config.accentColor,
                '--role-accent-soft': this.config.softColor,
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.role-dashboard__hero {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, var(--role-accent) 0%, #0f172a 100%);
}

.role-dashboard__hero::after {
  content: '';
  position: absolute;
  top: -40px;
  right: -40px;
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
}

.role-dashboard__eyebrow {
  color: rgba(255, 255, 255, 0.88);
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08rem;
  text-transform: uppercase;
}

.role-dashboard__hero-copy {
  max-width: 42rem;
  color: rgba(255, 255, 255, 0.82);
}

.role-dashboard__highlight {
  min-width: 9rem;
  padding: 0.9rem 1rem;
  margin-right: 0.75rem;
  margin-bottom: 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 0.9rem;
}

.role-dashboard__highlight-value {
  color: #fff;
  font-size: 1.35rem;
  font-weight: 700;
}

.role-dashboard__highlight-label {
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.78rem;
}

.role-dashboard__hero-aside {
  position: relative;
  z-index: 1;
  min-width: 15rem;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 1rem;
  color: #fff;
}

.role-dashboard__hero-aside-label {
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.06rem;
}

.role-dashboard__hero-aside-name {
  font-size: 1.2rem;
  font-weight: 700;
}

.role-dashboard__hero-aside-role {
  color: rgba(255, 255, 255, 0.8);
  font-weight: 600;
}

.role-dashboard__hero-aside-context {
  color: rgba(255, 255, 255, 0.76);
  font-size: 0.85rem;
}

.role-dashboard__context-card {
  background: linear-gradient(180deg, #ffffff 0%, var(--role-accent-soft) 100%);
}

.role-dashboard__context-pill {
  display: inline-flex;
  padding: 0.35rem 0.8rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.7);
  color: var(--role-accent);
  font-size: 0.76rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06rem;
}

.role-dashboard__context-box {
  padding: 1rem;
  background: rgba(255, 255, 255, 0.72);
  border-radius: 1rem;
}

.role-dashboard__stat-card,
.role-panel {
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.05);
}

.role-dashboard__stat-value {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.1;
}

.role-dashboard__stat-icon,
.role-action__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.8rem;
  height: 2.8rem;
  border-radius: 0.9rem;
  background: var(--role-accent-soft);
  color: var(--role-accent);
}

.role-panel__row {
  padding: 0.9rem 0;
  border-bottom: 1px solid rgba(15, 23, 42, 0.07);
}

.role-panel__row:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.role-timeline__item {
  position: relative;
  padding: 0 0 1.2rem 1.4rem;
}

.role-timeline__item:last-child {
  padding-bottom: 0;
}

.role-timeline__item::before {
  content: '';
  position: absolute;
  top: 0.45rem;
  left: 0;
  width: 0.6rem;
  height: 0.6rem;
  border-radius: 50%;
  background: var(--role-accent);
  box-shadow: 0 0 0 5px rgba(15, 118, 110, 0.12);
}

.role-timeline__item::after {
  content: '';
  position: absolute;
  top: 1rem;
  bottom: 0;
  left: 0.28rem;
  width: 1px;
  background: rgba(15, 23, 42, 0.08);
}

.role-timeline__item:last-child::after {
  display: none;
}

.role-action,
.role-status {
  height: 100%;
  padding: 1rem;
  background: #f8fafc;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 1rem;
}

.role-status__value {
  font-size: 1.25rem;
  font-weight: 700;
}

.role-dashboard__table th {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.05rem;
  text-transform: uppercase;
  color: #6e6b7b;
}

.role-dashboard__table td {
  padding-top: 0.85rem;
  padding-bottom: 0.85rem;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  font-weight: 500;
}

@media (max-width: 1199px) {
  .role-dashboard__hero-aside {
    min-width: 0;
  }
}
</style>
